// Vocabulary editor (Normal Core C15 — grounded vocabulary; MASTER-PRD §6.5.9
// / §12-6c, GROUNDING-PRD §4.3). The human surface for the Lexicon round-trip:
// the workspace re-labels its core business nouns + the Commitment primitive in
// its own words ("Customer"→"Client", "Commitment"→"Matter"), SAVE persists the
// override into the `vocab.lexicon` setting, and "Reset to standard" clears it
// back to the canonical taxonomy. "Re-derive from my business" applies the
// offline industry preset matched to the workspace's business description (zero
// LLM tokens). All data flows through the `vocab` contract — no raw fetch.
//
// The direction of translation runs toward the user: agents/slugs/tables/event
// types stay canonical forever; surfaces *speak* the Lexicon. This editor only
// touches the user-facing labels.

import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Input,
  SettingsSection,
} from "../../index";
import { FormField } from "../form-shell";
import { useApi, apiCall, ApiError } from "../../api/client";
import {
  vocabContract,
  type WorkspaceLexiconDto,
  type LexiconTermDto,
} from "../../api/contracts/vocab";

// ── The editable rows ─────────────────────────────────────────────────────────
// The bounded set the editor exposes: the four core business nouns
// (packages/core CoreNoun) + the Commitment primitive (C13). Each row reads/
// writes a specific path in the lexicon; everything else (streams, stages, per-
// kind) keeps its canonical default and is left to onboarding's card-walk.

type RowKind = "noun" | "commitment";

interface EditableRow {
  /** Stable id for React + the draft map. */
  id: string;
  kind: RowKind;
  /** For `noun` rows: the CoreNoun key under lexicon.nouns. */
  nounKey?: string;
  label: string;
  help: string;
}

const ROWS: EditableRow[] = [
  {
    id: "customer",
    kind: "noun",
    nounKey: "customer",
    label: "Customer",
    help: "The people you serve — client, patient, member, donor, guest.",
  },
  {
    id: "commitment",
    kind: "commitment",
    label: "Commitment",
    help: "What you've agreed to deliver — matter, case, order, job, project.",
  },
  {
    id: "teamMember",
    kind: "noun",
    nounKey: "teamMember",
    label: "Team member",
    help: "Your people — crew, staff, stylist, agent, clinician.",
  },
  {
    id: "deliverable",
    kind: "noun",
    nounKey: "deliverable",
    label: "Deliverable",
    help: "The generic unit of work — job, matter, order, program.",
  },
  {
    id: "supplier",
    kind: "noun",
    nounKey: "supplier",
    label: "Supplier",
    help: "Who you buy from — vendor, partner, distributor.",
  },
];

/** Read the LexiconTerm a row points at (or null when unset). */
function termForRow(lexicon: WorkspaceLexiconDto, row: EditableRow): LexiconTermDto | null {
  if (row.kind === "commitment") return lexicon.commitmentKinds?.commitment ?? null;
  return (row.nounKey ? lexicon.nouns?.[row.nounKey] : null) ?? null;
}

/** Provenance label + tone for the chip (the brief-meta pattern made visible). */
function provenanceChip(
  term: LexiconTermDto | null,
): { text: string; tone: "preset" | "you" | "ai" | "default" } {
  const source = term?.provenance?.source;
  if (source === "user") return { text: "You", tone: "you" };
  if (source === "preset") return { text: "From your business", tone: "preset" };
  if (source === "llm") return { text: "AI-suggested", tone: "ai" };
  return { text: "Standard", tone: "default" };
}

interface Draft {
  label: string;
  labelOne: string;
}

export function VocabularyEditor() {
  const query = useApi(vocabContract.get, {});
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const data = query.data;

  // Hydrate the draft map from the resolved lexicon whenever it (re)loads.
  useEffect(() => {
    if (!data) return;
    const next: Record<string, Draft> = {};
    for (const row of ROWS) {
      const term = termForRow(data.lexicon, row);
      next[row.id] = {
        label: term?.label ?? "",
        labelOne: term?.labelOne ?? "",
      };
    }
    setDrafts(next);
  }, [data]);

  // Build the WorkspaceLexicon to persist: start from the resolved lexicon
  // (preserves streams/stages/other kinds) and overlay the edited rows. A row
  // the user edited gets `provenance.source = "user"`.
  const buildLexicon = (): WorkspaceLexiconDto | null => {
    if (!data) return null;
    const lexicon: WorkspaceLexiconDto = JSON.parse(JSON.stringify(data.lexicon));
    for (const row of ROWS) {
      const d = drafts[row.id];
      if (!d) continue;
      const label = d.label.trim();
      if (!label) continue;
      const existing = termForRow(data.lexicon, row);
      const changed =
        label !== (existing?.label ?? "") ||
        d.labelOne.trim() !== (existing?.labelOne ?? "");
      const term: LexiconTermDto = {
        label,
        ...(d.labelOne.trim() ? { labelOne: d.labelOne.trim() } : {}),
        // Editing a term marks it user-owned; an untouched term keeps its
        // upstream provenance (preset / AI / canonical) so the chip stays honest.
        provenance: changed ? { source: "user" } : existing?.provenance,
      };
      if (row.kind === "commitment") {
        lexicon.commitmentKinds = { ...lexicon.commitmentKinds, commitment: term };
      } else if (row.nounKey) {
        lexicon.nouns = { ...lexicon.nouns, [row.nounKey]: term };
      }
    }
    return lexicon;
  };

  const dirty = useMemo(() => {
    if (!data) return false;
    return ROWS.some((row) => {
      const term = termForRow(data.lexicon, row);
      const d = drafts[row.id];
      if (!d) return false;
      return d.label.trim() !== (term?.label ?? "") ||
        d.labelOne.trim() !== (term?.labelOne ?? "");
    });
  }, [data, drafts]);

  const act = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const save = () =>
    act(async () => {
      const lexicon = buildLexicon();
      if (!lexicon) return;
      const r = await apiCall(vocabContract.save, { body: { lexicon } });
      if (!r.ok) throw new ApiError(400, r.reason ?? "Save refused");
      await query.mutate();
      setNotice("Saved — your workspace now speaks your language.");
    });

  const reset = () =>
    act(async () => {
      const r = await apiCall(vocabContract.reset, {});
      if (!r.ok) throw new ApiError(400, r.reason ?? "Reset refused");
      await query.mutate();
      setNotice("Reset to the standard vocabulary.");
    });

  const derive = () =>
    act(async () => {
      const r = await apiCall(vocabContract.derive, {});
      if (!r.ok) throw new ApiError(400, r.reason ?? "Could not re-derive");
      await query.mutate();
      setNotice(
        r.industry
          ? `Re-derived from your business (${r.industry}).`
          : "Re-derived from your business.",
      );
    });

  if (query.isLoading || !data) {
    return (
      <SettingsSection title="Vocabulary" description="Loading your terms…">
        <div className="type-body text-muted-foreground">Loading…</div>
      </SettingsSection>
    );
  }

  return (
    <SettingsSection
      id="vocabulary"
      title="Vocabulary"
      description="Excellent speaks your language. Rename the core business terms — a law firm has Clients and Matters, not Customers and Commitments. Slugs and data stay the same; only the words you see change."
      actions={
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={derive}
            disabled={busy || !data.business}
            title={
              data.business
                ? "Re-derive your vocabulary from your business description"
                : "Add a business description in onboarding to enable this"
            }
          >
            Re-derive from my business
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={reset}
            disabled={busy || !data.custom}
            title="Clear your overrides and use the standard vocabulary"
          >
            Reset to standard
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}
        {notice ? (
          <Alert>
            <AlertDescription>{notice}</AlertDescription>
          </Alert>
        ) : null}

        {!data.custom ? (
          <p className="type-body text-muted-foreground">
            You&rsquo;re on the standard vocabulary. Edit any term below and save
            to make it yours.
          </p>
        ) : null}

        <div className="space-y-0">
          {ROWS.map((row) => {
            const term = termForRow(data.lexicon, row);
            const chip = provenanceChip(term);
            const draft = drafts[row.id] ?? { label: "", labelOne: "" };
            return (
              <div
                key={row.id}
                className="border-b border-border/40 py-4 first:pt-0 last:border-b-0 last:pb-0"
              >
                <FormField
                  label={
                    <span className="flex items-center gap-2">
                      {row.label}
                      <Badge variant="outline">{chip.text}</Badge>
                    </span>
                  }
                  help={row.help}
                >
                  <div className="grid max-w-xl grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <span className="type-subtext text-muted-foreground">Plural</span>
                      <Input
                        value={draft.label}
                        onChange={(e) =>
                          setDrafts((d) => ({
                            ...d,
                            [row.id]: { ...draft, label: e.target.value },
                          }))
                        }
                        placeholder={term?.label ?? row.label + "s"}
                        disabled={busy}
                      />
                    </div>
                    <div className="space-y-1">
                      <span className="type-subtext text-muted-foreground">Singular</span>
                      <Input
                        value={draft.labelOne}
                        onChange={(e) =>
                          setDrafts((d) => ({
                            ...d,
                            [row.id]: { ...draft, labelOne: e.target.value },
                          }))
                        }
                        placeholder={term?.labelOne ?? row.label}
                        disabled={busy}
                      />
                    </div>
                  </div>
                </FormField>
              </div>
            );
          })}
        </div>

        <div className="flex items-center gap-2">
          <Button onClick={save} disabled={busy || !dirty}>
            Save vocabulary
          </Button>
          {dirty ? (
            <span className="type-subtext text-muted-foreground">Unsaved changes</span>
          ) : null}
        </div>
      </div>
    </SettingsSection>
  );
}
